import React, { useRef } from 'react';
import { cn } from 'shared/lib/cn';

export type SegOption = { value: string; label: string };
export type SegProps = { options: SegOption[]; value: string; onChange: (value: string) => void; ariaLabel: string; disabled?: boolean; className?: string };

/** Calculate the next index for arrow navigation, wrapping at boundaries. */
function nextIndex(key: string, current: number, length: number): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    return (current + 1) % length;
  }
  if (key === 'ArrowLeft' || key === 'ArrowUp') {
    return (current - 1 + length) % length;
  }
  if (key === 'Home') {
    return 0;
  }
  if (key === 'End') {
    return length - 1;
  }
  return null;
}

export function Seg({ options, value, onChange, ariaLabel, disabled, className }: SegProps) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // If value matches no option, the first option is focused.
  const currentIndex = options.findIndex((o) => o.value === value);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled) return;

    const next = nextIndex(e.key, index, options.length);
    if (next === null) return;

    const nextOption = options[next];
    if (!nextOption) return;

    e.preventDefault();
    onChange(nextOption.value);
    // Move DOM focus to the new option synchronously — the ref array is keyed
    // by the (unchanging) options order, so it already points at the right
    // button regardless of when the controlled `value` prop catches up.
    buttonRefs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('inline-flex rounded-[var(--radius-2)] border border-hairline/20 bg-surface-high p-0.5', className)}>
      {options.map((o, index) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            tabIndex={active || (currentIndex < 0 && index === 0) ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'rounded-[var(--radius-2)] px-3 py-1 font-body text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:opacity-50',
              active ? 'bg-brand text-ground' : 'text-ink-muted hover:text-ink',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
